import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { map, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { Utils } from '../../util/function';
import { ICompany } from '../../model/company.model';
import { EmployeeService } from '../../services/employee.service';
import { IUser } from '../../model/auth.model';
import { NotificationService } from '../../services/notification.service';
import { JwtService } from '../../services/jwt.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() sidebarToggle = new EventEmitter<void>();
  user: IUser | null = null;
  currentCompany: ICompany | null = null;
  notificationCount = 0;
  isNotificationBlinking = false;
  private blinkTimeout: ReturnType<typeof setTimeout> | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private employeeService: EmployeeService,
    private notificationService: NotificationService,
    private jwtService: JwtService,
    public util: Utils,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.authService
      .getCurrentUser$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.user = user as IUser;
        if (user) {
          this.currentCompany =
            user.companies?.find((c) => c.id === user.currentCompanyId) || null;
          this.loadNotificationCount();
        } else {
          this.currentCompany = null;
          this.notificationCount = 0;
        }
        if (!user && this.authService.isAuthenticated()) {
          this.authService.getProfile().subscribe();
        }
      });

    this.notificationService.newNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.user) {
          return;
        }

        const optimisticCount = this.notificationCount + 1;

        this.notificationCount = optimisticCount;
        this.startNotificationBlink();
        this.loadNotificationCount(optimisticCount);
      });
  }

  loadNotificationCount(minimumCount?: number): void {
    this.employeeService
      .getNotifications({ isSeen: false })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res.success) {
          this.notificationCount =
            minimumCount === undefined
              ? res.data.count
              : Math.max(res.data.count, minimumCount);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.blinkTimeout) {
      clearTimeout(this.blinkTimeout);
    }
  }

  private startNotificationBlink(): void {
    if (this.blinkTimeout) {
      clearTimeout(this.blinkTimeout);
    }

    this.isNotificationBlinking = false;

    setTimeout(() => {
      this.isNotificationBlinking = true;
      this.blinkTimeout = setTimeout(() => {
        this.isNotificationBlinking = false;
        this.blinkTimeout = null;
      }, 4200);
    });
  }

  toggleSidebar(): void {
    this.sidebarToggle.emit();
  }

  logout(): void {
    this.authService.logoutApi().subscribe((res) => {
      this.router.navigate(['/auth/login']);
    });
  }

  get isImpersonating(): boolean {
    const payload = this.jwtService.decodeJWT();
    return (
      payload?.tokenType === 'impersonation' && !!payload?.impersonatorId
    );
  }

  switchBackToAdmin(): void {
    if (!this.isImpersonating) {
      return;
    }

    this.employeeService
      .switchBackToAdmin()
      .pipe(
        tap((response) => {
          if (response.success && response.data.accessToken) {
            this.jwtService.saveJWT(response.data.accessToken);
          }
        }),
        switchMap((response) => {
          if (response.success && response.data.accessToken) {
            return this.authService
              .getProfile()
              .pipe(map((profileResponse) => profileResponse.data));
          }

          return of(null);
        }),
      )
      .subscribe((user) => {
        if (user) {
          this.router.navigate(['/dashboard']);
        }
      });
  }

  switchCompany(companyId: string): void {
    if (this.user && this.user.currentCompanyId === companyId) {
      return; // Do nothing if it's the same company
    }
    this.authService.switchCompany(companyId).subscribe((user) => {
      if (user) {
        window.location.reload();
      }
    });
  }

  makeImageURL(url: string | null | undefined): string {
    if (!url) return '';
    return this.util.makeImageURL(url);
  }
}
